import { CheckCircleFilled } from "@ant-design/icons";
import { Button, message } from "antd";
import { handleFacebookLogin } from "helpers/facebook-auth";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BusinessState, getBusinessInfo } from "redux-context/business";
import {
  connectSocialMedia,
  disconnectSocialMedia,
} from "services/SauceService";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import "./ConnectInstagram.scss";

const ConnectInstagram: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const { business } = useSelector((state: BusinessState) => state.business);
  const instagramAccounts = useMemo(() => {
    return (
      business?.socialMediasConnected?.filter((socialMedia) =>
        ["instagram", "meta"].includes(
          socialMedia?.socialMediaName?.toLowerCase()
        )
      ) || []
    );
  }, [business]);

  const isConnected = instagramAccounts.length > 0;

  const handleFacebookResponse = async (response: any) => {
    if (response.status === "connected") {
      const businessId = localStorage.getItem("businessId");
      const accessToken = response.authResponse.accessToken;
      setLoading(true);
      try {
        await connectSocialMedia({
          businessId,
          accessToken,
        });
        setLoading(false);
        message.success("Meta Business connected successfully");
        dispatch(getBusinessInfo());
      } catch (error: any) {
        console.log(error);
        message.error(
          error?.response?.data?.message || "Error connecting Meta Business"
        );
        setLoading(false);
      }
    } else {
      console.log("User is not logged in with Facebook");
    }
  };

  const onClickSigninMeta = () => {
    handleFacebookLogin(handleFacebookResponse);
  };

  const onClickDisconnectSocialMedia = async () => {
    try {
      setLoading(true);
      await disconnectSocialMedia({
        businessId: localStorage.getItem("businessId"),
        targetSocialMedia: "instagram",
      });
      message.success("Meta Business disconnected successfully");
      dispatch(getBusinessInfo());
    } catch (error: any) {
      console.log(error);
      message.error("Error disconnecting Meta Business");
    } finally {
      setLoading(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="connect-instagram-container">
        <div className="icon-container">
          <InstagramIcon className="instagram-icon" />
        </div>
        <div className="text-container">
          <p className="title">Meta Business (Instagram)</p>
          <p className="description">
            Connect your Meta Business account to access Instagram
          </p>
        </div>
        <div className="button-container">
          <Button
            type="default"
            loading={loading}
            className="connect-button"
            onClick={onClickSigninMeta}
          >
            Connect
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="connect-instagram-container connected">
      <div className="connected-header">
        <div className="icon-container">
          <InstagramIcon className="instagram-icon" />
        </div>
        <div className="text-container">
          <p className="title">
            Meta Business (Instagram){" "}
            <CheckCircleFilled className="check-icon" />
          </p>
          <p className="description">
            Connect your Meta Business account to access Instagram
          </p>
        </div>
        <div className="button-instagram-container">
          <Button
            type="link"
            className="disconnect-button"
            onClick={onClickDisconnectSocialMedia}
          >
            Disconnect
          </Button>
        </div>
      </div>

      {instagramAccounts.length > 0 && (
        <div className="accounts-container">
          <p className="accounts-title">
            {instagramAccounts.length}{" "}
            {instagramAccounts.length === 1 ? "account" : "accounts"} connected:
          </p>
          <div className="accounts-list">
            {instagramAccounts.map((account, index) => (
              <div key={account._id || index} className="account-badge">
                <InstagramIcon className="instagram-icon small" />
                {account.accountName ? `@${account.accountName}` : "Account"}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectInstagram;
